import React from 'react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { useAuth } from '@/providers/AuthProvider';

interface EnhancedTaskItemProps {
  node: ProseMirrorNode;
  updateAttributes: (attributes: Record<string, any>) => void;
  deleteNode: () => void;
  selected: boolean;
}

export function EnhancedTaskItemView({ node, updateAttributes }: EnhancedTaskItemProps) {
  const { user } = useAuth();
  const { checked, completedBy, completedAt } = node.attrs;

  const handleCheckboxChange = () => {
    const newChecked = !checked;
    const now = new Date().toISOString();

    updateAttributes({
      checked: newChecked,
      completedBy: newChecked ? user?.name || user?.email || 'Unknown User' : null,
      completedAt: newChecked ? now : null,
    });
  };

  const formatCompletionInfo = () => {
    if (!checked || !completedBy) return null;

    if (!completedAt) {
      return `Completed by ${completedBy}`;
    }

    const completedDate = new Date(completedAt);
    const now = new Date();
    const diffInHours = (now.getTime() - completedDate.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return `Completed by ${completedBy} just now`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `Completed by ${completedBy} ${hours}h ago`;
    } else {
      return `Completed by ${completedBy} on ${completedDate.toLocaleDateString()}`;
    }
  };

  return (
    <NodeViewWrapper className="task-item-wrapper">
      <div className="task-item flex items-start gap-2 my-1 p-1 rounded hover:bg-muted/50 transition-colors">
        <label className="flex items-center cursor-pointer mt-0.5">
          <input
            type="checkbox"
            checked={checked}
            onChange={handleCheckboxChange}
            className="task-checkbox w-4 h-4 border-2 border-border rounded appearance-none cursor-pointer transition-all duration-200 hover:border-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 checked:bg-primary checked:border-primary"
            style={{
              backgroundImage: checked ? `url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='m13.854 3.646-7.5 7.5a.5.5 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L6 9.293l7.146-7.147a.5.5 0 0 1 .708.708z'/%3e%3c/svg%3e")` : 'none',
              backgroundSize: '12px',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        </label>
        <div className="flex-1 min-w-0">
          <NodeViewContent
            className={`task-content ${checked ? 'line-through opacity-70' : ''}`}
          />
          {checked && completedBy && (
            <div className="text-xs text-muted-foreground mt-1 italic">
              {formatCompletionInfo()}
            </div>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
}